import {
  ContainerProps,
  Destructor,
  DnDProps,
  DropTargetObjects,
  ElementRef,
  OnSortEnd,
  SortableElementProps,
} from './types';
import { getGhostContainer } from './utils';

export const registerSortable = (
  ref: React.RefObject<HTMLElement | null>,
  props: SortableElementProps,
  setState: (props: DnDProps) => void
): Destructor => {
  if (sortableRefs.has(ref) || sortableRefsSetState.has(ref)) {
    throw Error('Trying to overwrite existing ref before clearing it');
  }
  sortableRefs.set(ref, { props, ref, setStateWithRefCheck: getSetState(ref) });
  sortableRefsSetState.set(ref, setState);
  return () => {
    sortableRefs.delete(ref);
    sortableRefsSetState.delete(ref);
  };
};

export const registerContainer = (
  ref: React.RefObject<HTMLElement | null>,
  props: ContainerProps
): Destructor => {
  if (containerRefs.has(ref)) {
    throw Error('Trying to overwrite existing ref before clearing it');
  }
  containerRefs.set(ref, props);
  return () => {
    containerRefs.delete(ref);
  };
};

const sortableRefs: Map<ElementRef, DropTargetObjects> = new Map();
const sortableRefsSetState: Map<ElementRef, (props: DnDProps) => void> =
  new Map();
const containerRefs: Map<ElementRef, ContainerProps> = new Map();

const getSetState = (ref: ElementRef) => (props: DnDProps) => {
  const setState = sortableRefsSetState.get(ref);
  if (!setState) {
    throw Error('Trying to set state on a dead React component');
  }
  setState(props);
};

export const getDropTargets = (_event: React.MouseEvent, ref: ElementRef) => {
  const container = ref.current;
  // TODO take collection into account?
  return Array.from(sortableRefs.values()).filter(({ ref: { current } }) =>
    container?.contains(current)
  );
};

export const getRefBoundContainerProps = (ref: ElementRef) => ({
  refBoundOnSortEnd: (update: Parameters<OnSortEnd>[0]) => {
    if (!containerRefs.has(ref)) {
      throw Error('Trying to call onSortEnd on a dead React component');
    }
    const { onSortEnd } = containerRefs.get(ref) ?? {};
    if (onSortEnd) {
      onSortEnd(update);
    }
  },
  refBoundGetGhostContainer: () => {
    if (!containerRefs.has(ref)) {
      throw Error('Trying to get helperContainer on a dead React component');
    }
    const { helperContainer } = containerRefs.get(ref) ?? {};
    return getGhostContainer(helperContainer);
  },
  isAutoScrollRefBound: () => {
    if (!containerRefs.has(ref)) {
      throw Error('Trying to get isAutoScroll on a dead React component');
    }
    const { isAutoScroll = false } = containerRefs.get(ref) ?? {};
    return isAutoScroll;
  },
});
